import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { Bias, BiasCategory } from '../../types/biases';
import { EASE_OUT } from '../../utils/motion';
type BiasCardProps = {
  bias: Bias;
};
const FRONT_BG: Record<BiasCategory, string> = {
  info: 'bg-bias-info',
  meaning: 'bg-bias-meaning',
  fast: 'bg-bias-fast',
  memory: 'bg-bias-memory'
};
export function BiasCard({
  bias
}: BiasCardProps) {
  const [flipped, setFlipped] = useState(false);
  return <button type="button" onClick={() => setFlipped(f => !f)} aria-pressed={flipped} aria-label={`${bias.name}. ${flipped ? 'Showing details. Press to show front.' : 'Press to reveal strategy, example and impact.'}`} className="group block aspect-[3/4] w-full rounded-[22px] text-left [perspective:1200px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bias-text/60 focus-visible:ring-offset-4 focus-visible:ring-offset-bias-bg" data-id="element-20" data-mp="2cpg4truxyu23">
      <motion.div className="relative h-full w-full [transform-style:preserve-3d]" animate={{
      rotateY: flipped ? 180 : 0
    }} transition={{
      duration: 0.3,
      ease: EASE_OUT
    }} data-id="element-21" data-mp="1twlxnk3k868f">
        <div className={`absolute inset-0 flex flex-col overflow-hidden rounded-[22px] p-5 text-night transition-transform duration-200 ease-out [backface-visibility:hidden] group-hover:-translate-y-1 ${FRONT_BG[bias.category]}`} data-id="element-22" data-mp="3nvnoq1oj4p2l">
          <span className="text-xs font-semibold tabular-nums" data-id="element-23" data-mp="3evqrvcrzkjwg">{bias.id}</span>
          <span aria-hidden className="pointer-events-none absolute right-4 top-4 select-none text-5xl font-semibold leading-none text-night/10 [writing-mode:vertical-rl]" data-id="element-24" data-mp="39vmlwgopf7kh">
            Cognitive
          </span>
          <h3 className="mt-auto pr-6 text-xl font-semibold leading-tight tracking-tight" data-id="element-25" data-mp="2zzt2qeghcftq">{bias.name}</h3>
        </div>

        <div className="absolute inset-0 flex flex-col overflow-hidden rounded-[22px] border border-bias-line bg-bias-panel p-5 text-bias-text [backface-visibility:hidden] [transform:rotateY(180deg)]" data-id="element-26" data-mp="y6bwnf7zzn13">
          <div className="flex items-center gap-2" data-id="element-27" data-mp="2xda7h307x2ng">
            <span className={`h-2 w-2 rounded-full ${FRONT_BG[bias.category]}`} data-id="element-28" data-mp="2xev1mrimejks" />
            <h3 className="text-sm font-semibold" data-id="element-29" data-mp="2ipfc1eutsk6o">{bias.name}</h3>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-bias-muted" data-id="element-30" data-mp="2nmxq1jnp9plj">{bias.definition}</p>
          <dl className="mt-auto space-y-2.5 pt-3" data-id="element-31" data-mp="3bdbaop3ygm63">
            {[['Strategy', bias.strategy], ['Example', bias.example], ['Impact', bias.impact]].map(([label, text]) => <div key={label} data-id="element-32" data-mp="32blgjbiov94m">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-bias-muted" data-id="element-33" data-mp="32eg070tvopwz">{label}</dt>
                <dd className="mt-0.5 text-xs leading-snug" data-id="element-34" data-mp="1qx7wyiuyxfue">{text}</dd>
              </div>)}
          </dl>
        </div>
      </motion.div>
    </button>;
}